import { create } from "zustand";
import { getProducts } from "../services/productService";


function resolveAttributeCounts(product, entry) {
  const attributes = Array.isArray(product?.attributes)
    ? product.attributes
    : (Array.isArray(entry?.attributes) ? entry.attributes : []);

  const derivedCount = attributes.length;
  const derivedActiveCount = attributes.filter((attribute) => attribute?.active !== false).length;

  const attribute_count = product?.attribute_count ?? product?.variant_count
    ?? entry?.attribute_count ?? entry?.variant_count ?? derivedCount;
  const active_attribute_count = product?.active_attribute_count ?? product?.active_variant_count
    ?? entry?.active_attribute_count ?? entry?.active_variant_count ?? derivedActiveCount;

  return { attribute_count, active_attribute_count };
}

function resolvePricing(product, entry) {
  if (product?.pricing ?? entry?.pricing) {
    return product?.pricing ?? entry?.pricing;
  }

  const basePriceCents = Number(product?.base_price ?? entry?.base_price ?? 0);
  if (basePriceCents > 0) {
    return { display: `$${(basePriceCents / 100).toFixed(2)}` };
  }

  return { display: "Price unavailable" };
}

function resolveImage(product, entry) {
  if (product?.image ?? entry?.image) {
    return product?.image ?? entry?.image;
  }

  const images = Array.isArray(product?.images)
    ? product.images
    : (Array.isArray(entry?.images) ? entry.images : []);

  return images[0] ?? null;
}

function normalizeProductPayload(rawProducts) {
  const list = Array.isArray(rawProducts) ? rawProducts : [];

  return list.flatMap((entry) => {
    const nestedProducts = Array.isArray(entry?.products) ? entry.products : [];

    if (nestedProducts.length > 0) {
      return nestedProducts.map((product) => {
        const { attribute_count, active_attribute_count } = resolveAttributeCounts(product, entry);
        return {
          ...product,
          id: product?.id ?? entry?.id ?? null,
          name: product?.name ?? entry?.name ?? "Untitled product",
          description: product?.description ?? entry?.description ?? "",
          pricing: resolvePricing(product, entry),
          attribute_count,
          active_attribute_count,
          variant_count: attribute_count,
          active_variant_count: active_attribute_count,
          image: resolveImage(product, entry),
        };
      });
    }

    const { attribute_count, active_attribute_count } = resolveAttributeCounts(entry, entry);
    return [{
      ...entry,
      id: entry?.id ?? null,
      name: entry?.name ?? "Untitled product",
      description: entry?.description ?? "",
      pricing: resolvePricing(entry, entry),
      attribute_count,
      active_attribute_count,
      variant_count: attribute_count,
      active_variant_count: active_attribute_count,
      image: resolveImage(entry, entry),
    }];
  });
}

const useProductCatalogStore = create((set, get) => ({
  products: [],
  loading: false,
  error: null,

  setProducts: (nextProducts) => set({
    products: normalizeProductPayload(nextProducts),
    error: null,
  }),

  fetchProducts: async () => {
    set({ loading: true, error: null });

    try {
      const payload = await getProducts();
      const normalized = normalizeProductPayload(payload?.products ?? payload ?? []);
      set({ products: normalized, loading: false, error: null });
      return normalized;
    } catch (error) {
      console.error("Failed to fetch catalog products:", error);
      set({ products: [], loading: false, error: error?.message || "Failed to fetch products" });
      return [];
    }
  },

  clearProducts: () => set({ products: [], loading: false, error: null }),
}));

export default useProductCatalogStore;
